import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitch, useToast } from '@/components/common';
import { errorMessage } from '@/lib/api-client';
import { useAuth } from '../auth';

function PasswordForm() {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProfile } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateProfile({ password: next, currentPassword: current });
      setCurrent('');
      setNext('');
      toast.show(t('profile.passwordChanged'));
    } catch (err) {
      toast.show(errorMessage(err, { VALIDATION: t('profile.wrongPassword') }), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card__head">
        <h2>{t('profile.password')}</h2>
      </div>
      <form className="stack" onSubmit={submit}>
        <div className="field">
          <label htmlFor="p-current">{t('profile.currentPassword')}</label>
          <input
            id="p-current"
            className="input"
            type="password"
            required
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="p-next">{t('profile.newPassword')}</label>
          <input
            id="p-next"
            className="input"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </div>
        <div className="row">
          <button type="submit" className="btn btn--dark" disabled={busy}>
            {t('profile.changePassword')}
          </button>
        </div>
      </form>
    </section>
  );
}

/** The signed-in user's own details, default language and password. */
export function ProfilePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { user, changeLanguage, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name ?? '');
  const [license, setLicense] = useState(user?.licenseNumber ?? '');
  const [busy, setBusy] = useState(false);
  if (!user) return null;
  const changed = name.trim() !== user.name || license.trim() !== (user.licenseNumber ?? '');

  const saveName = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateProfile({ name: name.trim(), licenseNumber: license.trim() || null });
      toast.show(t('settings.saved'));
    } catch (err) {
      toast.show(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack profile">
      <div className="page-head">
        <div className="page-head__title">
          <h1>{t('profile.title')}</h1>
        </div>
      </div>
      <section className="card">
        <div className="card__head">
          <h2>{t('profile.details')}</h2>
        </div>
        <form className="stack" onSubmit={saveName}>
          <div className="field">
            <label htmlFor="p-name">{t('settings.users.name')}</label>
            <input
              id="p-name"
              className="input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {user.treatsPatients && (
            <div className="field">
              <label htmlFor="p-license">{t('settings.users.licenseNumber')}</label>
              <input
                id="p-license"
                className="input"
                autoComplete="off"
                value={license}
                onChange={(e) => setLicense(e.target.value)}
              />
              <span className="small muted">{t('settings.users.licenseHint')}</span>
            </div>
          )}
          <dl className="facts">
            <dt>{t('settings.users.email')}</dt>
            <dd>{user.email}</dd>
            <dt>{t('settings.users.role')}</dt>
            <dd>{t(`common.role.${user.role}`)}</dd>
            {user.clinic && (
              <>
                <dt>{t('profile.clinic')}</dt>
                <dd>{user.clinic.name}</dd>
              </>
            )}
          </dl>
          <div className="row">
            <button
              type="submit"
              className="btn btn--dark"
              disabled={busy || !name.trim() || !changed}
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </section>
      <section className="card">
        <div className="card__head">
          <h2>{t('profile.language')}</h2>
        </div>
        <p className="small muted">{t('profile.languageHint')}</p>
        <LanguageSwitch onChange={changeLanguage} />
      </section>
      <PasswordForm />
    </div>
  );
}
